import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export interface BreadcrumbItem {
  label: string;
  routeName: string;
  params?: Record<string, string>;
  url: string;
  disabled?: boolean;
  meta?: {
    entityType?: 'category' | 'contributor' | 'course' | 'lesson' | 'level';
    entitySlug?: string;
    entityTitle?: string;
  };
}

export const useNavigationStore = defineStore('navigation', () => {
  // Store navigation chain as array of breadcrumb items
  const navigationChain = ref<BreadcrumbItem[]>([]);

  // Maximum breadcrumb depth to prevent memory issues
  const MAX_BREADCRUMB_DEPTH = 5;

  /**
   * Add a navigation item to the chain
   * Automatically deduplicates and manages chain length
   */
  function addToNavigationChain(item: BreadcrumbItem) {
    // Remove duplicate if same route already exists in chain
    const existingIndex = navigationChain.value.findIndex(
      (existing) => existing.url === item.url
    );

    if (existingIndex !== -1) {
      // Truncate chain at the duplicate (user went "back" in their navigation)
      navigationChain.value = navigationChain.value.slice(0, existingIndex);
    }

    // Add new item
    navigationChain.value.push(item);

    // Limit chain length
    if (navigationChain.value.length > MAX_BREADCRUMB_DEPTH) {
      navigationChain.value = navigationChain.value.slice(-MAX_BREADCRUMB_DEPTH);
    }

    // Persist to localStorage
    persistNavigationChain();
  }

  /**
   * Replace the last item in the navigation chain
   * Used for sibling navigation (e.g., lesson → lesson within same course)
   */
  function replaceLastInNavigationChain(item: BreadcrumbItem) {
    if (navigationChain.value.length > 0) {
      navigationChain.value.pop();
    }
    navigationChain.value.push(item);
    persistNavigationChain();
  }

  /**
   * Build breadcrumb items for the current page
   * @param currentPageLabel - Label for the current (disabled) breadcrumb item
   */
  function buildBreadcrumbs(currentPageLabel: string): BreadcrumbItem[] {
    const breadcrumbs = [...navigationChain.value];

    // Add current page as disabled item
    breadcrumbs.push({
      label: currentPageLabel,
      routeName: '',
      url: '',
      disabled: true,
    });

    return breadcrumbs;
  }

  /**
   * Clear navigation chain (e.g., when user logs out or navigates to home)
   */
  function clearNavigationChain() {
    navigationChain.value = [];
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('navigation_chain');
    }
  }

  /**
   * Persist navigation chain to localStorage
   */
  function persistNavigationChain() {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('navigation_chain', JSON.stringify(navigationChain.value));
    }
  }

  /**
   * Restore navigation chain from localStorage
   */
  function restoreNavigationChain() {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem('navigation_chain');
      if (stored) {
        try {
          navigationChain.value = JSON.parse(stored);
        } catch (e) {
          console.error('Failed to restore navigation chain:', e);
          clearNavigationChain();
        }
      }
    }
  }

  /**
   * Update a specific item in the navigation chain
   * @param routeName - The route name to find and update
   * @param updates - Partial breadcrumb item to merge with existing item
   */
  function updateNavigationChainItem(routeName: string, updates: Partial<BreadcrumbItem>) {
    const index = navigationChain.value.findIndex(item => item.routeName === routeName);
    if (index !== -1) {
      navigationChain.value[index] = {
        ...navigationChain.value[index],
        ...updates,
      };
      persistNavigationChain();
    }
  }

  // Initialize from localStorage on store creation
  restoreNavigationChain();

  return {
    navigationChain: computed(() => navigationChain.value),
    addToNavigationChain,
    replaceLastInNavigationChain,
    buildBreadcrumbs,
    clearNavigationChain,
    restoreNavigationChain,
    updateNavigationChainItem,
  };
});
